
import isEmpty from 'lodash/isEmpty'
import pickBy from 'lodash/pickBy'

import { generateAlternateLinks } from '@core/helpers/head'
import logger from '@logger'

import {
  DEFAULT_LAYOUT_FIELDS,
  PAGE_TYPE_WITH_SPECIFIC_FIELDS,
} from './Landing.config'
import translations from './Landing.translations'
import { OWNER_FROM_PAGE_TYPE } from './helpers/constants'
import {
  DESCRIPTION_TAGS,
  IMAGE_TAGS,
  NOINDEX_META_TAG,
  TITLE_TAGS,
  createMetaForGroupedTags,
} from './helpers/meta'
import DefaultLayout from './layouts/Default'
import { requestCMSPageConfigFromRoute, sanitizeRouteParams } from './service'

export default {
  components: {
    DefaultLayout,
  },

  async asyncData({ route, store, redirect, error }) {
    try {
      const { payload } = await store.dispatch(
        'http/request',
        requestCMSPageConfigFromRoute({ route }),
      )

      const cleanParams = sanitizeRouteParams({
        parentPageInfos: payload.parentPageInfos,
        route,
      })

      if (!isEmpty(cleanParams)) {
        return redirect(301, {
          ...route,
          params: {
            ...route.params,
            ...cleanParams,
          },
        })
      }

      const linksCountries = payload.meta.alternateLinks.map(
        ({ country: linkCountry }) => linkCountry,
      )

      const alternateLinks = await generateAlternateLinks({
        link: route,
        baseUrl: store.getters['config/baseURL'],
        countries: linksCountries,
      })

      // [ACQ-1482]
      // Note for the front-apps migration: consider creating a new layout without cookie modal
      // Having 2 different children won't be necessary
      const specificFields = pickBy(
        payload,
        (_, fieldName) =>
          !Object.keys(DEFAULT_LAYOUT_FIELDS).includes(fieldName),
      )

      return {
        ...payload,
        alternateLinks,
        specificFields,
        useSpecificLayout: PAGE_TYPE_WITH_SPECIFIC_FIELDS.includes(
          payload.pageType,
        ),
      }
    } catch (err) {
      const {
        pathParams: { pageType, pageName, pageCategory: category },
      } = requestCMSPageConfigFromRoute({ route })

      const owner = OWNER_FROM_PAGE_TYPE[pageType] || ''
      const pageCategory = category ? `[${category}]` : ''

      const message = `${owner}[${pageType}][${pageName}]${pageCategory} ${err.message}`
      logger.error(message, err)

      return error({
        statusCode: err.status_code || 404,
        message: err.message || '',
      })
    }
  },

  data() {
    return {
      ...DEFAULT_LAYOUT_FIELDS,
      specificFields: {},
      useSpecificLayout: false,
    }
  },

  head() {
    const { titleSeo, meta: cmsMeta } = this
    const { title, description, image } = cmsMeta
    const imageUrl = image.src

    const meta = [
      ...createMetaForGroupedTags([
        {
          tags: DESCRIPTION_TAGS,
          content: description,
        },
        {
          tags: TITLE_TAGS,
          content: title,
        },
        {
          tags: IMAGE_TAGS,
          content: imageUrl,
        },
      ]),
      ...(this.hideFromSearchEngine === true ? [NOINDEX_META_TAG] : []),
    ]

    return {
      title: titleSeo,
      titleTemplate: `%s | ${this.$t(translations.companyName)}`,
      meta,
      link: this.alternateLinks,
    }
  },

  /**
   * Nuxt doesn't merge the component's asyncData with its data after navigating to a child page using the same component.
   * We have to do it ourselves to trigger the re-rendering of the page.
   * see: https://github.com/nuxt/nuxt.js/issues/2591
   */
  watch: {
    $route() {
      Object.assign(this.$data, this.$options.data())
    },
  },
}
